import { Input as InputField } from '@components'
import { FormControl } from '@mui/material'
import Box from '@mui/material/Box'
import { type FC, useCallback } from 'react'
import {
	Controller,
	type ControllerRenderProps,
	type FieldPath,
	useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { FormValues } from '../Calculator'

interface InputProps {
	fieldName: FieldPath<FormValues>
	label: string
	unit?: string
	number?: boolean
	disabled?: boolean
}

const KNOWN_ERROR_TYPES = ['min', 'required'] as const

const Input: FC<InputProps> = ({
	fieldName,
	label,
	unit,
	number,
	disabled = false,
}: InputProps) => {
	const {
		control,
		/* eslint-disable @typescript-eslint/no-unused-vars */
		formState: { errors },
		getFieldState,
	} = useFormContext()

	const { t } = useTranslation()

	const { invalid, error } = getFieldState(fieldName)

	const errorType = error?.type
		? KNOWN_ERROR_TYPES.includes(
				error.type as (typeof KNOWN_ERROR_TYPES)[number]
			)
			? (error.type as (typeof KNOWN_ERROR_TYPES)[number])
			: 'warning'
		: undefined

	const renderField = useCallback(
		({ field }: { field: ControllerRenderProps }) => (
			<FormControl variant="outlined" fullWidth sx={{ p: 0, m: 0, mt: -0.75 }}>
				<InputField
					{...field}
					label={label}
					disabled={disabled}
					error={invalid}
					helperText={
						invalid &&
						errorType &&
						t('calculator.errors.message', { context: errorType })
					}
					unit={unit}
					inputRef={field.ref}
					variant="filled"
					fullWidth
					type={number ? 'number' : 'string'}
				/>
			</FormControl>
		),
		[disabled, errorType, invalid, label, number, t, unit]
	)
	return (
		<Box display="flex">
			<Controller name={fieldName} control={control} render={renderField} />
		</Box>
	)
}

export default Input
